import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppRootContext } from '@telegram-apps/telegram-ui/dist/hooks/useAppRootContext';
import styles from './AgentListPage.module.css';
import { getPage, post } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';
import { RechargeLogModal } from '../RechargeLogModal/RechargeLogModal';
import { RechargeModal } from '../RechargeModal/RechargeModal';
import { CreateProxyModal } from '../CreateProxyModal/CreateProxyModal';
import { AgentOverview } from '../AgentOverview/AgentOverview';
import { OperateLogModal } from '../OperateLogModal/OperateLogModal';

type ProxyAdmin = {
  id: number;
  username: string;
  realname: string;
  nickname: string;
  telegram: string;
  balance: number;
  creditLimit?: number;
  status: number;
  createdAt: string;
};

const PAGE_SIZE = 6;
const AVATAR_COLORS = ['#1677ff', '#7257e8', '#ff9d27', '#e5484d', '#20a9b5'];

const displayName = (proxy: ProxyAdmin) =>
  proxy.realname || proxy.nickname || proxy.username || `代理 #${proxy.id}`;

const formatDate = (value: string) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) {
    return value.slice(0, 10);
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatYuan = (n: number) =>
  `¥${Number(n || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const AgentsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="9" cy="7.5" r="3.6" />
    <path d="M3 20a6 6 0 0 1 12 0" />
    <path d="M19 8v6" />
    <path d="M16 11h6" />
  </svg>
);

export const AgentListPage = () => {
  const { portalContainer } = useAppRootContext();
  const [agents, setAgents] = useState<ProxyAdmin[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingDir, setLoadingDir] = useState<'prev' | 'next' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<ProxyAdmin | null>(null);
  const [logProxy, setLogProxy] = useState<ProxyAdmin | null>(null);
  const [operateProxy, setOperateProxy] = useState<ProxyAdmin | null>(null);
  const [rechargeProxy, setRechargeProxy] = useState<ProxyAdmin | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);

  const fetchAgents = useCallback(async (pageNum: number, dir: 'prev' | 'next' | null = null) => {
    setLoading(true);
    setLoadingDir(dir);
    setError(null);
    try {
      const result = await getPage<ProxyAdmin>(
        `/admin/tel/proxy/list?page=${pageNum}&limit=${PAGE_SIZE}`
      );
      setAgents(result.records);
      setPage(result.current);
      setPages(result.pages);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
      setLoadingDir(null);
    }
  }, []);

  useEffect(() => {
    fetchAgents(1);
  }, [fetchAgents]);

  const handleRefresh = () => {
    setSpinning(true);
    fetchAgents(page);
    setTimeout(() => setSpinning(false), 600);
  };

  const confirmToggle = useCallback(async () => {
    if (!confirmTarget || togglingId !== null) {
      return;
    }
    const agent = confirmTarget;
    const nextStatus = Number(agent.status) === 1 ? 0 : 1;
    setTogglingId(agent.id);
    setError(null);
    try {
      await post<number>('/admin/admin/updateStatus', { id: agent.id, status: nextStatus });
      setAgents((list) =>
        list.map((item) =>
          item.id === agent.id ? { ...item, status: nextStatus } : item
        )
      );
      setConfirmTarget(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '操作失败');
    } finally {
      setTogglingId(null);
    }
  }, [confirmTarget, togglingId]);

  return (
    <>
      <AgentOverview />
      <div className="card">
      <div className={`card-header ${styles.compactHeader}`}>
        <div className="section-title">
          <button
            type="button"
            className={`icon-box ${styles.iconBtn}`}
            onClick={() => setCreateOpen(true)}
            aria-label="新增代理"
          >
            <AgentsIcon />
          </button>
          <button
            type="button"
            className={styles.titleBtn}
            onClick={() => setCreateOpen(true)}
          >
            代理列表
          </button>
        </div>
        <div className="card-header-right">
          <button type="button" className="card-refresh" onClick={handleRefresh}>
            <svg
              className={spinning ? 'card-refresh-spin' : ''}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v5h-5" />
            </svg>
          </button>
          <div className="section-tag">{total} 个</div>
        </div>
      </div>
      <div className={styles.head}>
        <div className={styles.headInner}>
          <span className={styles.headSpacer} />
          <span className={styles.headName}>代理</span>
          <span className={styles.headBalance}>余额</span>
          <span className={styles.headDate}>注册时间</span>
          <span className={styles.headAction}>操作</span>
        </div>
      </div>
      <div className={`${styles.list} ${loading ? styles.loading : ''} ${spinning ? styles.refreshing : ''}`}>
        {agents.length > 0 &&
          agents.map((agent, index) => (
            <div
              key={agent.id}
              className={styles.item}
              onClick={() => setLogProxy(agent)}
            >
              <span
                className={`${styles.avatar} ${styles.avatarClickable}`}
                style={{ background: AVATAR_COLORS[index % AVATAR_COLORS.length] }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOperateProxy(agent);
                }}
              >
                {firstChineseChar(displayName(agent))}
              </span>
              <div className={styles.info}>
                <span className={styles.name}>{displayName(agent)}</span>
                <span className={styles.sub}>{agent.username || `代理 #${agent.id}`}</span>
              </div>
              <div className={styles.balanceCell}>
                <span className={`${styles.balance} ${Number(agent.balance) < 10 ? styles.low : ''}`}>
                  {formatYuan(agent.balance)}
                </span>
                <span className={styles.credit}>
                  <span className={styles.creditBadge}>C</span>
                  {Number(agent.creditLimit ?? 0).toLocaleString('zh-CN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <span className={styles.date}>{formatDate(agent.createdAt)}</span>
              <button
                type="button"
                className={`${styles.action} ${Number(agent.status) === 1 ? styles.disable : styles.enable}`}
                disabled={togglingId === agent.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmTarget(agent);
                }}
              >
                {togglingId === agent.id
                  ? '...'
                  : Number(agent.status) === 1
                    ? '禁用'
                    : '启用'}
              </button>
            </div>
          ))}
        {agents.length === 0 && !loading && !error && (
          <div className={styles.empty}>暂无代理</div>
        )}
      </div>
      {error && <div className="error">{error}</div>}
      {pages > 1 && (
        <div className={`pagination ${styles.paginationTight}`}>
          <button
            type="button"
            className="page-btn prev"
            disabled={loading || page <= 1}
            onClick={() => fetchAgents(page - 1, 'prev')}
          >
            {loading && loadingDir === 'prev' ? <span className="spinner" /> : <span>&lt;</span>}
            上一页
          </button>
          <span className="page-number">{page} / {pages}</span>
          <button
            type="button"
            className="page-btn next"
            disabled={loading || page >= pages}
            onClick={() => fetchAgents(page + 1, 'next')}
          >
            下一页
            {loading && loadingDir === 'next' ? <span className="spinner" /> : <span>&gt;</span>}
          </button>
        </div>
      )}
      <RechargeLogModal
        open={logProxy !== null}
        onOpenChange={(next) => {
          if (!next) {
            setLogProxy(null);
          }
        }}
        proxyId={logProxy?.id ?? 0}
        proxyName={logProxy ? displayName(logProxy) : ''}
        balance={logProxy?.balance ?? 0}
        onRecharge={() => {
          const target = logProxy;
          setLogProxy(null);
          if (target) {
            setRechargeProxy(target);
          }
        }}
      />
      <RechargeModal
        open={rechargeProxy !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRechargeProxy(null);
          }
        }}
        proxyId={rechargeProxy?.id ?? 0}
        proxyName={rechargeProxy ? displayName(rechargeProxy) : ''}
        balance={rechargeProxy?.balance ?? 0}
        onSuccess={() => fetchAgents(page)}
      />
      <OperateLogModal
        open={operateProxy !== null}
        onOpenChange={(next) => {
          if (!next) {
            setOperateProxy(null);
          }
        }}
        proxyId={operateProxy?.id ?? 0}
        proxyName={operateProxy ? displayName(operateProxy) : ''}
      />
      {confirmTarget &&
        portalContainer?.current &&
        createPortal(
          <div
            className={styles.sheetOverlay}
            onClick={() => togglingId === null && setConfirmTarget(null)}
          >
            <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
              <div
                className={`${styles.sheetBanner} ${
                  Number(confirmTarget.status) === 1 ? styles.sheetBannerDanger : styles.sheetBannerPrimary
                }`}
              >
                <div className={styles.sheetBannerTitle}>
                  {Number(confirmTarget.status) === 1 ? '禁用代理' : '启用代理'}
                </div>
                <div className={styles.sheetBannerSub}>
                  {Number(confirmTarget.status) === 1 ? '禁用后该代理将无法登录' : '启用后该代理恢复登录权限'}
                </div>
                <div className={styles.sheetBannerBars} aria-hidden="true">
                  <i /><i /><i />
                </div>
              </div>
              <div className={styles.sheetBody}>
                <div className={styles.sheetText}>
                  确定要{Number(confirmTarget.status) === 1 ? '禁用' : '启用'}
                  <span className={styles.sheetName}>{displayName(confirmTarget)}</span>
                  吗？
                </div>
                <div className={styles.sheetDesc}>
                  {Number(confirmTarget.status) === 1
                    ? '禁用后，该代理将无法登录小程序，也无法进行任何充值、激活等操作，请谨慎操作。'
                    : '启用后，该代理将恢复登录权限，可以正常使用小程序进行充值与激活等操作。'}
                </div>
              </div>
              <div className={styles.sheetActions}>
                <button
                  type="button"
                  className={`${styles.sheetBtn} ${styles.sheetCancel}`}
                  disabled={togglingId !== null}
                  onClick={() => setConfirmTarget(null)}
                >
                  取消
                </button>
                <button
                  type="button"
                  className={`${styles.sheetBtn} ${
                    Number(confirmTarget.status) === 1 ? styles.sheetDanger : styles.sheetPrimary
                  }`}
                  disabled={togglingId !== null}
                  onClick={confirmToggle}
                >
                  {togglingId !== null
                    ? '处理中...'
                    : Number(confirmTarget.status) === 1
                      ? '确认禁用'
                      : '确认启用'}
                </button>
              </div>
            </div>
          </div>,
          portalContainer.current
        )}
      <CreateProxyModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => {
          setCreateOpen(false);
          fetchAgents(1);
        }}
      />
      </div>
    </>
  );
};
